import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { message, Modal } from 'antd';
import { SegmentGroup } from '@fxtr/i18n';
import config from '$config';
import Loading from '$app/shared/components/Loading';
import { useMaiaT } from '$app/i18n/useMaiaT';
import { getStoredSelectedSegment, SEGMENT_SELECTION_CHANGED_EVENT } from '$app/segmentSelection';
import GaragePageHeader from '../components/GaragePageHeader';
import makeGarageFrom from '../components/GarageForm';
import { createGarage, prepareGarageCreatePayload } from './actions';
import { mapPropsToFields, getErrorMessages } from '../../utils';
import { normalizeOptionalStrings } from '../../utils/normalizeOptionalStrings';
import { normalizePhoneFields } from '../../utils/phoneValidation';

/** @type {any} antd Form.create() HOC erases the inner component's prop types. */
const GarageCreateForm = makeGarageFrom(mapPropsToFields);
// Partner segment groups (MOTRIO and future ones like RENAULT) inherit and lock the
// segment from the top-bar selection. Only FIXTER keeps the manually selectable dropdown.
const inheritsSegmentSelection = config.segmentGroup !== SegmentGroup.FIXTER;

export function GarageCreatePage(props) {
  const t = useMaiaT();
  const { history } = props;
  const [state, setState] = useState({
    loading: false,
  });

  // For partner segment groups, the segment is inherited from the top-bar selection and
  // locked so every new garage is created under the segment the user is working in.
  // Kept in state so it stays in sync when the user changes the top-bar selection.
  const [lockedSegment, setLockedSegment] = useState(
    inheritsSegmentSelection ? getStoredSelectedSegment() : undefined
  );

  useEffect(() => {
    if (!inheritsSegmentSelection) return undefined;
    const handleSegmentChange = () => setLockedSegment(getStoredSelectedSegment());
    window.addEventListener(SEGMENT_SELECTION_CHANGED_EVENT, handleSegmentChange);
    return () => window.removeEventListener(SEGMENT_SELECTION_CHANGED_EVENT, handleSegmentChange);
  }, []);

  if (state.loading) return <Loading />;

  function onSubmit(e, form) {
    form.validateFieldsAndScroll(async (err, values) => {
      if (!err) {
        setState((st) => ({ ...st, loading: true }));
        try {
          const payload = await prepareGarageCreatePayload(
            normalizeOptionalStrings(normalizePhoneFields(values))
          );
          const garage = await createGarage(payload);
          message.success(t('maia.garageCreate.success', 'Garage created successfully'));
          history.push(`/${garage.garageId}`);
        } catch (error) {
          Modal.error({
            title: t('maia.garageCreate.failedTitle', 'Garage creation failed'),
            content: (
              <div>
                {getErrorMessages(error).map((msg) => (
                  <p style={{ fontSize: 12, marginBottom: 5 }} key={msg[0]}>
                    {msg}
                  </p>
                ))}
              </div>
            ),
          });
          setState((st) => ({ ...st, loading: false }));
        }
      }
    });
  }

  return (
    <>
      <GaragePageHeader history={history} />
      <GarageCreateForm onSubmit={onSubmit} loading={state.loading} lockedSegment={lockedSegment} />
    </>
  );
}

export default withRouter(GarageCreatePage);
